/**
 * Display-frame schedule for the rateless stream.
 *
 * Frames are mapped deterministically to global stream indices so the UI can
 * pre-render ahead and re-request any frame:
 *
 *   - a manifest is re-broadcast every manifestPeriod frames, so a receiver
 *     that joins mid-transfer configures itself quickly;
 *   - the first pass sends every source symbol once, parallelCount per frame;
 *   - after that, every resendEvery-th frame re-sends a slice of source for
 *     late joiners, and the remaining tiles carry fresh repair symbols with
 *     monotonically increasing ordinals.
 */
import type { QrSender } from './sender';

export interface QrStreamScheduleOptions {
  parallelCount: number;
  manifestPeriod?: number;
  resendEvery?: number;
}

export class QrStreamSchedule {
  private readonly sender: QrSender;
  private readonly parallel: number;
  private readonly manifestPeriod: number;
  private readonly resendEvery: number;
  private readonly sourceCount: number;
  private readonly firstPassFrames: number;

  constructor(sender: QrSender, options: QrStreamScheduleOptions) {
    this.sender = sender;
    this.parallel = Math.max(1, Math.floor(options.parallelCount));
    this.manifestPeriod = Math.max(1, options.manifestPeriod ?? 24);
    this.resendEvery = Math.max(1, options.resendEvery ?? 3);
    this.sourceCount = sender.sourcePackets.length;
    this.firstPassFrames = Math.max(1, Math.ceil(this.sourceCount / this.parallel));
  }

  get parallelCount(): number {
    return this.parallel;
  }

  get sourceFrames(): number {
    return this.firstPassFrames;
  }

  frameIndices(frame: number): number[] {
    if (!Number.isInteger(frame) || frame < 0) {
      throw new RangeError('invalid frame index: ' + frame);
    }
    const out: number[] = [];
    if (frame % this.manifestPeriod === 0) out.push(0);

    if (frame < this.firstPassFrames) {
      for (let tile = 0; tile < this.parallel && out.length < this.parallel; tile++) {
        const index = 1 + frame * this.parallel + tile;
        if (index <= this.sourceCount) out.push(index);
      }
    } else {
      const loopFrame = frame - this.firstPassFrames;
      const resendTiles = Math.max(1, Math.floor(this.parallel / 2));
      for (let tile = 0; tile < this.parallel && out.length < this.parallel; tile++) {
        const slot = loopFrame * this.parallel + tile;
        if (loopFrame % this.resendEvery === 0 && tile < resendTiles) {
          out.push(1 + (slot % this.sourceCount));
        } else {
          out.push(1 + this.sourceCount + slot);
        }
      }
    }

    while (out.length < this.parallel) {
      out.push(1 + this.sourceCount + frame * this.parallel + out.length);
    }
    return out;
  }

  framePackets(frame: number): Uint8Array[] {
    return this.frameIndices(frame).map((index) => this.sender.packetAt(index));
  }
}
